import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { useRefresh } from '../../contexts/RefreshContext';
import MentionInput from '../ui/MentionInput';
import { Button, Select, FormField, Alert } from '../ui';
import { JournalEntry } from '../../types/journal';
import { Project } from '../../types/project';
import { Business } from '../../types/business';
import { Category } from '../../types/category';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface JournalFormProps {
  isOpen: boolean;
  onClose: () => void;
  onJournalEntryCreated: () => void;
  initialEntry?: JournalEntry | null;
  isEditMode?: boolean;
  onJournalEntryUpdated?: (updatedEntry: JournalEntry) => void;
}

interface FormData {
  content: string;
  emotion: string;
  entry_type: string;
  project_id?: number | null;
  business_id?: number | null;
  category_ids?: number[];
  related_people?: number[];
}

const initialFormData: FormData = {
  content: '',
  emotion: '',
  entry_type: 'thought',
  project_id: null,
  business_id: null,
  category_ids: [],
  related_people: [],
};

// Define emotions and their corresponding emojis
const emotionsMap: { [key: string]: string } = {
  happy: '😊',
  sad: '😢',
  angry: '😠',
  surprised: '😮',
  calm: '😌',
  anxious: '😟',
  excited: '🤩',
  tired: '😴',
  confused: '😕',
};

// Define entry types
const entryTypes = [
  { value: 'thought', label: 'Thought' },
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'reference', label: 'Reference' },
];

const JournalForm: React.FC<JournalFormProps> = ({
  isOpen,
  onClose,
  onJournalEntryCreated,
  initialEntry = null,
  isEditMode = false,
  onJournalEntryUpdated,
}) => {
  const { refreshJournal } = useRefresh();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    if (initialEntry && isEditMode) {
      setFormData({
        content: initialEntry.content || '',
        emotion: initialEntry.emotion || '',
        entry_type: initialEntry.entry_type || 'thought',
        project_id: initialEntry.project?.id || null,
        business_id: (initialEntry as any).business?.id || null,
        category_ids: ((initialEntry as any).categories || []).map((cat: Category) => cat.id),
        related_people: [], // TODO: Extract person IDs from content mentions if needed
      });
    } else if (isOpen) {
      setFormData(initialFormData);
    }
  }, [isOpen, initialEntry, isEditMode]);

  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          const [projectsRes, businessesRes, categoriesRes] = await Promise.all([
            apiClient.get('/api/projects/'),
            apiClient.get('/api/businesses/'),
            apiClient.get('/api/categories/?type=general'),
          ]);
          setProjects(projectsRes.data);
          setBusinesses(businessesRes.data);
          setCategories(categoriesRes.data);
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setSubmitError(null);
        setSubmitSuccess(false);
      }, 300);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Handle project selection with automatic business/category inheritance
    if (name === 'project_id') {
      const projectId = value ? parseInt(value) : null;
      const selectedProject = projectId ? projects.find(p => p.id === projectId) : null;

      setFormData(prev => ({
        ...prev,
        project_id: projectId,
        // Auto-inherit business and categories from project
        business_id: selectedProject?.business?.id || prev.business_id,
        category_ids: selectedProject?.categories?.map((cat: Category) => cat.id) || prev.category_ids,
      }));
    } else if (name === 'business_id') {
      setFormData(prev => ({
        ...prev,
        business_id: value ? parseInt(value) : null,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handleCategoryChange = (categoryId: number) => {
    setFormData(prev => {
      const currentIds = prev.category_ids || [];
      const isSelected = currentIds.includes(categoryId);

      return {
        ...prev,
        category_ids: isSelected
          ? currentIds.filter(id => id !== categoryId)
          : [...currentIds, categoryId]
      };
    });
  };

  // Handler for selecting an emotion emoji
  const handleEmotionSelect = (emotionName: string) => {
    setFormData(prev => ({
        ...prev,
        // Toggle selection: if clicking the same one, clear it; otherwise, set it.
        emotion: prev.emotion === emotionName ? '' : emotionName
    }));
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    const payload = {
      content: formData.content,
      emotion: formData.emotion,
      entry_type: formData.entry_type,
      project_write: formData.project_id,
      business_write: formData.business_id,
      category_ids: formData.category_ids || [],
      related_people_ids: formData.related_people || [],
    };

    try {
      let response;
      if (initialEntry?.id && isEditMode) {
        response = await apiClient.patch(`/api/journal_entries/${initialEntry.id}/`, payload);
      } else {
        response = await apiClient.post('/api/journal_entries/', payload);
      }

      if (response.status >= 200 && response.status < 300) {
        setSubmitSuccess(true);
        if (isEditMode && onJournalEntryUpdated) onJournalEntryUpdated(response.data);
        else onJournalEntryCreated();
        refreshJournal();
        setTimeout(() => onClose(), 1500);
      } else {
        throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} journal entry (${response.status})`);
      }
    } catch (err: unknown) {
      const errorMsg = err instanceof Error
        ? err.message
        : `Failed to ${isEditMode ? 'update' : 'create'} journal entry.`;
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
      if (!isSubmitting) { onClose(); }
  }, [isSubmitting, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.25)' }} />
        </Transition.Child>

        <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center'
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '32rem',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: '16px',
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                verticalAlign: 'middle',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: SPACING[16]
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.headings.sizes.h2,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                    fontFamily: TYPOGRAPHY.headings.fontFamily
                  }}>
                    {isEditMode ? 'Edit Journal Entry' : 'Create New Journal Entry'}
                  </Dialog.Title>
                  <button
                    type="button"
                    style={{
                      color: COLORS.text.tertiary,
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      opacity: isSubmitting ? 0.5 : 1
                    }}
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon style={{ height: '24px', width: '24px' }} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                  <div>
                    <label htmlFor="content" style={{
                      display: 'block',
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.secondary,
                      fontFamily: TYPOGRAPHY.body.fontFamily
                    }}>
                      Content
                    </label>
                    <MentionInput
                      value={formData.content}
                      onChange={(value, selectedPersonIds) => setFormData(prev => ({
                        ...prev,
                        content: value,
                        related_people: selectedPersonIds
                      }))}
                      placeholder="Type @ to mention someone..."
                      rows={6}
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.secondary,
                      marginBottom: SPACING[4],
                      fontFamily: TYPOGRAPHY.body.fontFamily
                    }}>
                      Emotion (Optional)
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING[8] }}>
                      {Object.entries(emotionsMap).map(([name, emoji]) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => handleEmotionSelect(name)}
                          style={{
                            padding: SPACING[8],
                            borderRadius: '50%',
                            fontSize: TYPOGRAPHY.size['3xl'],
                            transition: 'transform 100ms ease-in-out',
                            transform: formData.emotion === name ? 'scale(1.1)' : 'scale(1)',
                            backgroundColor: formData.emotion === name ? '#DBEAFE' : COLORS.bg.hover,
                            boxShadow: formData.emotion === name ? `0 0 0 2px ${COLORS.accent.default}` : 'none',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          title={name.charAt(0).toUpperCase() + name.slice(1)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>

                  <FormField label="Entry Type" htmlFor="entry_type">
                    <Select
                      id="entry_type"
                      name="entry_type"
                      value={formData.entry_type}
                      onChange={handleInputChange}
                    >
                      {entryTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField label="Project (Optional)" htmlFor="project_id">
                    <Select
                      id="project_id"
                      name="project_id"
                      value={formData.project_id || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">No project</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </Select>
                    {formData.project_id && (
                      <p style={{
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        color: COLORS.text.secondary,
                        marginTop: SPACING[4],
                        fontFamily: TYPOGRAPHY.body.fontFamily
                      }}>
                        Business and categories will be inherited from the selected project
                      </p>
                    )}
                  </FormField>

                  <FormField label="Business (Optional)" htmlFor="business_id">
                    <Select
                      id="business_id"
                      name="business_id"
                      value={formData.business_id || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">No business</option>
                      {businesses.map((business) => (
                        <option key={business.id} value={business.id}>
                          {business.name}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.secondary,
                      marginBottom: SPACING[8],
                      fontFamily: TYPOGRAPHY.body.fontFamily
                    }}>
                      Categories (Optional)
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING[8] }}>
                      {categories.map((category) => {
                        const isSelected = (formData.category_ids || []).includes(category.id);
                        return (
                          <button
                            key={category.id}
                            type="button"
                            onClick={() => handleCategoryChange(category.id)}
                            style={{
                              paddingLeft: SPACING[12],
                              paddingRight: SPACING[12],
                              paddingTop: SPACING[4],
                              paddingBottom: SPACING[4],
                              borderRadius: '9999px',
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              transition: 'background-color 150ms',
                              backgroundColor: isSelected ? COLORS.accent.default : COLORS.bg.hover,
                              color: isSelected ? COLORS.bg.panel : COLORS.text.secondary,
                              border: 'none',
                              cursor: 'pointer',
                              fontFamily: TYPOGRAPHY.body.fontFamily
                            }}
                          >
                            {category.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginTop: SPACING[24], display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
                    {submitSuccess && <Alert variant="success">Entry created!</Alert>}
                    {submitError && <Alert variant="error">{submitError}</Alert>}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING[12] }}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        isLoading={isSubmitting}
                        loadingText="Saving..."
                      >
                        Save Entry
                      </Button>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default JournalForm;
